const uniqueIds = (values = []) => {
  const seen = new Set();
  const ids = [];
  values.forEach((raw) => {
    const id = Number(raw);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
};

const mermaidLabel = (text = "") => String(text)
  .replace(/"/g, "'")
  .replace(/[[\]{}]/g, "")
  .replace(/\n/g, "<br/>");

const nodeId = (prefix, ...parts) => `${prefix}_${parts.map((part) => String(part).replace(/[^a-zA-Z0-9]/g, "_")).join("_")}`;

const userLabel = (id, pool = [], fallbackName = "") => {
  const match = (pool || []).find((item) => Number(item.id) === Number(id));
  return match?.user_name || fallbackName || String(id);
};

const moduleLabel = (moduleId, catalogModules = [], fallback = "") => {
  const match = (catalogModules || []).find((item) => Number(item.id) === Number(moduleId));
  return match?.key || match?.process || fallback || String(moduleId);
};

const rowsForUserModule = (rows = [], userId, moduleId) =>
  (rows || []).filter((row) =>
    Number(row.user_id) === Number(userId)
    && Number(row.catalogmodule_id) === Number(moduleId)
  );

const linkedValidatorIds = (editorRow) => uniqueIds(
  (editorRow.Validators_To_FlowEditorConfig || []).map((item) => item.validator_user_id)
);

const linkedManagerIds = (validatorRow) => uniqueIds(
  (validatorRow.Managers_To_FlowValidatorConfig || []).map((item) => item.manager_user_id)
);

const usersWithConfigInModule = (rows = [], moduleId) => uniqueIds(
  (rows || [])
    .filter((row) => Number(row.catalogmodule_id) === Number(moduleId))
    .map((row) => row.user_id)
);

export const buildFlowModuleTreeChart = ({
  editorUserId,
  editorConfigs = [],
  validatorConfigs = [],
  managerConfigs = [],
  editorUsers = [],
  validatorUsers = [],
  managerUsers = [],
  preUsers = [],
  catalogModules = [],
  labels = {}
}) => {
  const editorId = Number(editorUserId);
  if (!editorId) {
    return { chart: "", notes: [] };
  }

  const catchAll = labels.catchAll || "todos del módulo";
  const noRecord = labels.noRecord || "sin ficha";
  const preTitle = labels.preTitle || "Pre-Gestores (transversales)";
  const notes = [];

  const editorRows = (editorConfigs || []).filter((row) => Number(row.user_id) === editorId);
  const editorName = userLabel(
    editorId,
    editorUsers,
    editorRows[0]?.User_To_FlowEditorConfig?.user_name
  );

  if (!editorRows.length) {
    notes.push(labels.noConfig || "Este editor no tiene ficha en el filtro actual.");
  }

  const lines = [
    "flowchart TB",
    `  ${nodeId("e", editorId)}["${mermaidLabel(`Editor · ${editorName}`)}"]`
  ];
  const edges = [];
  const leafIds = [];

  editorRows.forEach((editorRow) => {
    const moduleId = Number(editorRow.catalogmodule_id);
    const moduleName = moduleLabel(
      moduleId,
      catalogModules,
      editorRow.CatalogModule_To_FlowEditorConfig?.key
        || editorRow.CatalogModule_To_FlowEditorConfig?.process
    );
    const subgraphId = nodeId("mod", moduleId);
    const explicitValidators = linkedValidatorIds(editorRow);
    const isCatchAllValidators = explicitValidators.length === 0;
    const validatorIds = isCatchAllValidators
      ? usersWithConfigInModule(validatorConfigs, moduleId)
      : explicitValidators;

    lines.push(`  subgraph ${subgraphId}["${mermaidLabel(moduleName)}"]`);

    if (!validatorIds.length) {
      const emptyId = nodeId("emptyv", editorId, moduleId);
      lines.push(`    ${emptyId}["${mermaidLabel(isCatchAllValidators ? `Sin verificadores (${noRecord})` : "Sin verificadores vinculados")}"]`);
      edges.push(`  ${nodeId("e", editorId)} --> ${emptyId}`);
      leafIds.push(emptyId);
      lines.push("  end");
      return;
    }

    validatorIds.forEach((validatorId) => {
      const validatorRows = rowsForUserModule(validatorConfigs, validatorId, moduleId);
      const validatorName = userLabel(
        validatorId,
        validatorUsers,
        validatorRows[0]?.User_To_FlowValidatorConfig?.user_name
      );
      const validatorSuffix = isCatchAllValidators
        ? `<br/>(${catchAll})`
        : (!validatorRows.length ? `<br/>(${noRecord})` : "");
      const vNode = nodeId("v", validatorId, moduleId);
      lines.push(`    ${vNode}["${mermaidLabel(`Verificador · ${validatorName}`)}${validatorSuffix}"]`);
      edges.push(`  ${nodeId("e", editorId)} --> ${vNode}`);

      if (!validatorRows.length) {
        leafIds.push(vNode);
        return;
      }

      const explicitManagers = uniqueIds(validatorRows.flatMap((row) => linkedManagerIds(row)));
      const isCatchAllManagers = validatorRows.every((row) => linkedManagerIds(row).length === 0);
      const managerIds = isCatchAllManagers
        ? usersWithConfigInModule(managerConfigs, moduleId)
        : explicitManagers;

      if (!managerIds.length) {
        const emptyG = nodeId("emptyg", validatorId, moduleId);
        lines.push(`    ${emptyG}["${mermaidLabel(isCatchAllManagers ? `Sin gestores (${noRecord})` : "Sin gestores vinculados")}"]`);
        edges.push(`  ${vNode} --> ${emptyG}`);
        leafIds.push(emptyG);
        return;
      }

      managerIds.forEach((managerId) => {
        const managerName = userLabel(
          managerId,
          managerUsers,
          (managerConfigs || []).find((row) =>
            Number(row.user_id) === Number(managerId)
            && Number(row.catalogmodule_id) === moduleId
          )?.User_To_FlowManagerConfig?.user_name
        );
        const managerSuffix = isCatchAllManagers ? `<br/>(${catchAll})` : "";
        const gNode = nodeId("g", managerId, moduleId);
        lines.push(`    ${gNode}["${mermaidLabel(`Gestor · ${managerName}`)}${managerSuffix}"]`);
        edges.push(`  ${vNode} --> ${gNode}`);
        leafIds.push(gNode);
      });
    });

    lines.push("  end");
  });

  lines.push(...edges);

  if (preUsers.length) {
    const preIds = preUsers.map((user) => nodeId("p", user.id));
    lines.push(`  subgraph ${nodeId("pre")}["${mermaidLabel(preTitle)}"]`);
    lines.push("    direction LR");
    preUsers.forEach((user) => {
      const name = user.user_name || userLabel(user.id, preUsers);
      lines.push(`    ${nodeId("p", user.id)}["${mermaidLabel(`Pre · ${name}`)}"]`);
    });
    if (preIds.length > 1) {
      lines.push(`    ${preIds.join(" ~~~ ")}`);
    }
    lines.push("  end");
    const anchors = leafIds.length ? leafIds : [nodeId("e", editorId)];
    anchors.forEach((leafId) => {
      lines.push(`  ${leafId} ~~~ ${preIds[0]}`);
    });
  } else {
    notes.push(labels.noPre || "No hay Pre-Gestores con rol en este filtro.");
  }

  lines.push("  classDef editor fill:#1b4f72,color:#fff,stroke:#154360");
  lines.push("  classDef validator fill:#1a5276,color:#fff,stroke:#154360");
  lines.push("  classDef manager fill:#196f3d,color:#fff,stroke:#145a32");
  lines.push("  classDef pre fill:#6c3483,color:#fff,stroke:#512e5f");
  lines.push(`  class ${nodeId("e", editorId)} editor`);
  if (preUsers.length) {
    preUsers.forEach((user) => {
      lines.push(`  class ${nodeId("p", user.id)} pre`);
    });
  }

  return { chart: lines.join("\n"), notes };
};
